<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-besc-card shadow-sm">

    <!-- Section corrections demandées -->
    <div v-if="corrections.length > 0" class="alert alert-warning mb-4">
      <h6 class="text-primary fw-semibold mb-3">
        <i class="fas fa-exclamation-triangle me-2"></i>
        Corrections demandées ({{ corrections.length }})
      </h6>
      
      <div class="corrections-list">
        <div v-for="correction in corrections" :key="correction.id" 
             class="d-flex align-items-center justify-content-between p-2 mb-2 bg-light rounded">
          <div class="flex-grow-1">
            <small class="fw-semibold">
                    <span v-if="typeof correction.fields_concerned === 'object'">
                      {{ Object.keys(correction.fields_concerned).join(', ') }}
                    </span>
                    <span v-else>{{ correction.fields_concerned }}</span>
                  </small>
            <p class="mb-0 small text-muted">{{ correction.message_to_requester }}</p>
          </div>
          <button @click="markCorrectionAsFixed(correction.id)" 
                  class="btn btn-success btn-sm">
            <i class="fas fa-check me-1"></i>
            Corrigé
          </button>
        </div>
      </div>
    </div>

    <div class="card-header d-flex justify-content-between align-items-center py-4 px-4"
      style="background-color: #0a59a4;">
      <div>
        <h3 class="text-white fw-bold mb-1">Modification - Étape 2 – Gestion des Bills of Lading (BL)</h3>
        <small class="text-white opacity-75">Modifiez les BLs associés à ce dossier BESC</small>
      </div>
    </div>

    <div class="card-body p-15 p-sm-20 p-md-30 letter-spacing">

      <div v-if="chargementDonnees" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement...</span>
        </div>
        <p class="mt-3 text-muted">Chargement des BLs existants...</p>
      </div>

      <Form v-else ref="blForm" @submit="mettreAJour" :validation-schema="blSchema">
        <div v-if="listeBLs.length > 0" class="mb-4">

          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="text-primary fw-bold mb-0">
              <i class="fas fa-file-invoice me-2"></i>
              Bills of Lading ({{ listeBLs.length }})
            </h5>
             <button type="button" class="btn btn-sm" :class="peutAjouterBL ? 'btn-success' : 'btn-secondary'"
              :disabled="!peutAjouterBL" @click="ajouterBL">
              <i class="fas fa-plus me-2"></i>Ajouter un BL
            </button>
          </div>

          <div class="row g-4">
            <div v-for="(bl, index) in listeBLs" :key="bl.id || index" class="col-12">
              <div class="card border shadow-sm">

                <div class="card-header bg-light d-flex justify-content-between align-items-center py-3">
                  <div class="d-flex align-items-center gap-3">
                    <span class="badge bg-primary fs-6">BL #{{ index + 1 }}</span>
                    <strong class="text-dark">{{ bl.numeroBL || '(Non renseigné)' }}</strong>
                  </div>
                  <button type="button" class="btn btn-danger btn-sm" @click="supprimerBL(index)"
                    :disabled="listeBLs.length === 1">
                    <i class="fas fa-trash-alt"></i> Supprimer
                  </button>
                </div>

                <div class="card-body">

                  <div class="section-title mb-4">
                    <h6 class="text-primary fw-semibold border-bottom pb-2">
                      <i class="fas fa-info-circle me-2"></i>Informations du BL
                    </h6>
                  </div>

                  <div class="row g-3 mb-4">
                    <div class="col-md-6">
                      <label class="text-black fw-semibold mb-2">Numéro de BL <span class="text-danger">*</span></label>
                      <Field :name="`listeBLs[${index}].numeroBL`" v-model="bl.numeroBL" type="text"
                        class="form-control shadow-sm" placeholder="ex: MAEU123456789" @blur="() => validerBL(index)" />
                      <ErrorMessage :name="`listeBLs[${index}].numeroBL`" class="text-danger small mt-1 d-block" />
                    </div>

                    <div class="col-md-6">
                      <label class="text-black fw-semibold mb-2">Référence de voyage</label>
                      <Field :name="`listeBLs[${index}].referenceVoyage`" v-model="bl.referenceVoyage" type="text"
                        class="form-control shadow-sm" placeholder="ex: 024E" />
                    </div>
                  </div>

                  <div class="section-title mb-4">
                    <h6 class="text-primary fw-semibold border-bottom pb-2">
                      <i class="fas fa-ship me-2"></i>Informations de transport
                    </h6>
                  </div>

                  <div class="row g-3 mb-4">
                    <div class="col-md-4">
                      <label class="text-black fw-semibold mb-2">Compagnie maritime <span
                          class="text-danger">*</span></label>

                      <Field :name="`listeBLs[${index}].idCompagnie`" v-model="listeBLs[index].idCompagnie"
                        v-slot="{ field, errorMessage }">
                        <Multiselect v-bind="field" :options="compagniesMaritimes" :searchable="true"
                          :loading="chargementCompagnies" placeholder="Rechercher (code)..." label="label"
                          track-by="value" valueProp="value" @search-change="rechercherCompagnie"
                          :filter-results="false" :min-chars="2" />
                        <small v-if="errorMessage" class="text-danger">{{ errorMessage }}</small>
                      </Field>
                    </div>
                    <div class="col-md-4">
                      <label class="text-black fw-semibold mb-2">Navire</label>

                      <Field :name="`listeBLs[${index}].idNavire`" v-model="listeBLs[index].idNavire"
                        v-slot="{ field, errorMessage, setValue }">
                        <Multiselect 
                          v-bind="field" 
                          :options="listeNavires" 
                          :searchable="true"
                          :loading="chargementNavires" 
                          placeholder="Rechercher (IMO/MMSI)..." 
                          label="label"
                          track-by="value" 
                          valueProp="value" 
                          @search-change="rechercherNavire" 
                          :filter-results="false"
                          :min-chars="2"
                          @update:model-value="async (val) => {
                            setValue(val);
                            const navire = listeNavires.find(n => n.value === val);
                            if (navire?.imo) {
                              const result = await checkBlacklist('SHIP', navire.imo);
                              if (result.isBlacklisted) {
                                blacklistAlerts.navires[index] = result.details;
                              } else {
                                delete blacklistAlerts.navires[index];
                              }
                            } else {
                              delete blacklistAlerts.navires[index];
                            }
                          }" />
                        <small v-if="errorMessage" class="text-danger">{{ errorMessage }}</small>
                      </Field>
                    </div>

                    <div class="col-md-4">
                      <label class="text-black fw-semibold mb-2">
                        Poids brut total (Tonnes) 
                        <span class="text-muted">(calculé auto)</span>
                      </label>
                      <div class="input-group">
                        <input 
                          type="number" 
                          :value="calculerPoidsBrutBL(bl).toFixed(2)"
                          class="form-control bg-light fw-bold" 
                          readonly 
                          disabled
                        />
                        <span class="input-group-text" :class="calculerPoidsBrutBL(bl) > 0 ? 'bg-success text-white' : 'bg-secondary text-white'">
                          <i class="fas fa-weight-hanging"></i>
                        </span>
                      </div>
                      <small class="d-block mt-2" :class="calculerPoidsBrutBL(bl) > 0 ? 'text-success' : 'text-warning'">
                        <i class="fas" :class="calculerPoidsBrutBL(bl) > 0 ? 'fa-check-circle' : 'fa-info-circle'"></i>
                        <span v-if="calculerPoidsBrutBL(bl) > 0">
                          Calculé depuis {{ bl.marchandises?.length || 0 }} marchandise(s)
                        </span>
                        <span v-else>
                          Ajoutez des marchandises pour calculer le poids
                        </span>
                      </small>
                    </div>
                  </div>

                  <!-- Alerte blacklist pour le navire -->
                  <div v-if="blacklistAlerts.navires[index]" class="row g-3 mb-4">
                    <div class="col-md-12">
                      <div class="alert alert-danger text-center py-3 px-4">
                        <h6 class="fw-bold mb-2">
                          <i class="ph-warning-circle fs-4"></i> ⚠️ ALERTE BLACKLIST - NAVIRE
                        </h6>
                        <div class="text-start">
                          <strong>Navire:</strong> Liste {{ blacklistAlerts.navires[index].list_code }} - {{ blacklistAlerts.navires[index].reason }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="bl.erreurs?.marchandises" class="alert alert-warning mt-3">
                    <i class="fas fa-exclamation-triangle me-2"></i>
                    {{ bl.erreurs.marchandises }}
                  </div>

                  <div class="accordion mt-4" :id="`accordéonBL-${index}`">
                    <div class="accordion-item border">
                      <h2 class="accordion-header">
                        <button class="accordion-button collapsed fw-semibold" type="button" data-bs-toggle="collapse"
                          :data-bs-target="`#contenuBL-${index}`" :aria-controls="`contenuBL-${index}`"
                          aria-expanded="false" style="background-color: #f8f9fa;">
                          <i class="fas fa-boxes me-2 text-primary"></i>
                          Gestion des marchandises et conteneurs
                          <span class="ms-3 badge" style="background-color: #0a59a4;">{{ bl.marchandises?.length || 0 }}
                            marchandise(s)</span>
                        </button>
                      </h2>

                      <div :id="`contenuBL-${index}`" class="accordion-collapse collapse"
                        :data-bs-parent="`#accordéonBL-${index}`">
                        <div class="accordion-body bg-light">

                          <!-- Boutons d'ajout -->
                          <div class="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-4">
                            <button type="button" class="btn btn-info shadow-sm" @click="ouvrirModalMarchandise(index)">
                              <i class="fas fa-box me-2"></i>Ajouter une marchandise
                            </button>
                          </div>

                          <div v-if="bl.marchandises && bl.marchandises.length > 0" class="mb-4">
                            <h6 class="fw-bold mb-3" style="color: #0a59a4;">
                              <i class="fas fa-container-storage me-2"></i>
                              Conteneurs ({{ calculerConteneurUniques(bl) }})
                            </h6>

                            <div class="table-responsive">
                              <table class="table table-hover table-bordered bg-white">
                                <thead class="table-info">
                                  <tr>
                                    <th>N°</th>
                                    <th>Numéro Conteneur</th>
                                    <th>Type</th>
                                    <th>Scellé</th>
                                    <th>Type Chargement</th>
                                    <th>Poids Brut (Tonnes)</th>
                                    <th>Volume (m³)</th>
                                    <th>Marchandises Associées</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="(conteneur, cIndex) in obtenirConteneursUniques(bl)" :key="cIndex">
                                    <td>{{ cIndex + 1 }}</td>
                                    <td>
                                      <strong class="text-primary">{{ conteneur.numeroConteneur }}</strong>
                                    </td>
                                    <td>
                                      {{ typeConteneurs.find(t => t.value === conteneur.typeConteneur)?.label || '-' }}
                                    </td>
                                    <td>{{ conteneur.numeroScelle || '-' }}</td>
                                    <td>
                                      {{ typeChargements.find(t => t.value === conteneur.typeChargement)?.label || '-' }}
                                    </td>
                                    <td class="text-end">{{ conteneur.poidsBrut?.toFixed(2) || '0.00' }}</td>
                                    <td class="text-end">{{ conteneur.volume?.toFixed(2) || '-' }}</td>
                                    <td class="text-center">
                                      <button 
                                        type="button" 
                                        class="btn btn-sm btn-outline-success"
                                        @click="afficherMarchandisesConteneur(index, conteneur.numeroConteneur)"
                                        :title="`Voir les ${conteneur.marchandises.length} marchandise(s)`">
                                        <i class="fas fa-boxes me-1"></i>
                                        {{ conteneur.marchandises.length }} marchandise(s)
                                      </button>
                                    </td>
                                  </tr>
                                </tbody>
                                <tfoot class="table-light">
                                  <tr>
                                    <td colspan="5" class="text-end fw-bold">Total:</td>
                                    <td class="text-end fw-bold">
                                      {{ bl.marchandises.reduce((sum, m) => sum + (m.poidsBrut || 0), 0).toFixed(2) }} tonnes
                                    </td>
                                    <td class="text-end fw-bold">
                                      {{ bl.marchandises.reduce((sum, m) => sum + (m.valeurDeclaree || 0), 0).toFixed(2) }} USD
                                    </td>
                                    <td class="text-center fw-bold">
                                      <i class="fas fa-boxes me-1"></i>
                                      {{ bl.marchandises.length }} marchandise(s) total
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>

                            <!-- Marchandises en vrac (sans conteneur) -->
                            <div v-if="obtenirMarchandisesVrac(bl).length > 0" class="mt-4">
                              <h6 class="fw-bold mb-3 text-warning">
                                <i class="fas fa-box-open me-2"></i>
                                Marchandises en vrac ({{ obtenirMarchandisesVrac(bl).length }})
                              </h6>
                              <div class="table-responsive">
                                <table class="table table-hover table-bordered bg-light">
                                  <thead class="table-warning">
                                    <tr>
                                      <th>N°</th>
                                      <th>Description</th>
                                      <th>Code HS</th>
                                      <th>Type Conditionnement</th>
                                      <th>Poids (Tonnes)</th>
                                      <th>Valeur</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="marchandise in obtenirMarchandisesVrac(bl)" :key="marchandise.originalIndex">
                                      <td>{{ marchandise.originalIndex + 1 }}</td>
                                      <td>{{ marchandise.description }}</td>
                                      <td>
                                        <small class="text-muted">
                                          {{
                                            typeof marchandise.hscode === 'object'
                                              ? (marchandise.hscode?.label || `${marchandise.hscode?.code} - ${marchandise.hscode?.product}`)
                                              : hsCodes.find(h => h.value === marchandise.hscode)?.label || '-'
                                          }}
                                        </small>
                                      </td>
                                      <td>
                                        {{ marchandise.nombreColis }} {{ typeConditionnements.find(t => t.value === marchandise.typeConditionnement)?.label || '-' }}
                                      </td>
                                      <td class="text-end">{{ marchandise.poidsBrut.toFixed(2) }}</td>
                                      <td class="text-end">{{ marchandise.valeurDeclaree.toFixed(2) }} {{ marchandise.devise }}</td>
                                      <td>
                                        <button 
                                          type="button" 
                                          class="btn btn-outline-primary btn-sm" 
                                          @click="modifierMarchandise(index, marchandise.originalIndex)"
                                          title="Modifier">
                                          <i class="flaticon-edit"></i>
                                        </button>
                                        <button type="button" class="btn btn-outline-danger btn-sm"
                                          @click="supprimerMarchandise(index, marchandise.originalIndex)" title="Supprimer">
                                          <i class="flaticon-delete"></i>
                                        </button>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>

                          <div v-if="!bl.marchandises || !bl.marchandises.length" class="text-center py-4">
                            <div class="alert alert-info">
                              <i class="fas fa-info-circle me-2"></i>
                              <strong>Aucune marchandise ajoutée</strong>
                              <p class="mb-0 mt-2 small">Cliquez sur "Ajouter une marchandise" pour commencer.</p>
                            </div>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- <div v-else class="text-center py-5">
          <i class="fas fa-file-invoice fa-5x text-muted opacity-50 mb-4"></i>
          <h5 class="text-muted">Aucun BL ajouté pour le moment</h5>
          <p class="text-muted mb-4">Commencez par ajouter un Bill of Lading</p>
          <button type="button" class="btn btn-primary px-4" @click="ajouterBL">
            <i class="fas fa-plus me-2"></i>Ajouter le premier BL
          </button>
        </div> -->

        <div v-if="listeBLs.length > 0" class="mt-5 p-4 bg-light rounded border">
          <h6 class="fw-bold mb-3"><i class="fas fa-chart-bar me-2"></i>Résumé</h6>
          <div class="row g-3 text-center text-md-start">
            <div class="col-md-3 col-6">
              <div class="d-flex align-items-center gap-2 justify-content-center justify-content-md-start">
                <i class="fas fa-file-invoice text-primary fs-4"></i>
                <div>
                  <small class="text-muted d-block">Total BLs</small>
                  <strong class="fs-4">{{ listeBLs.length }}</strong>
                </div>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div class="d-flex align-items-center gap-2 justify-content-center justify-content-md-start">
                <i class="fas fa-weight text-success fs-4"></i>
                <div>
                  <small class="text-muted d-block">Poids total</small>
                  <strong class="fs-4">{{ poidsTotalBrut.toFixed(2) }} tonnes</strong>
                </div>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div class="d-flex align-items-center gap-2 justify-content-center justify-content-md-start">
                <i class="fas fa-ship text-warning fs-4"></i>
                <div>
                  <small class="text-muted d-block">Compagnies</small>
                  <strong class="fs-4">{{ nombreCompagniesUniques }}</strong>
                </div>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div class="d-flex align-items-center gap-2 justify-content-center justify-content-md-start">
                <i class="fas fa-check-circle text-info fs-4"></i>
                <div>
                  <small class="text-muted d-block">BLs complets</small>
                  <strong class="fs-4">{{ nombreBLValides }} / {{ listeBLs.length }}</strong>
                  <small class="d-block text-muted" style="font-size: 0.7rem;">
                    (avec marchandises)
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="d-flex flex-column flex-md-row justify-content-between align-items-center mt-5 pt-4 border-top gap-3">
          <!-- <router-link
            v-if="idBesc"
            :to="{ name: 'ViewBesc', params: { id: idBesc } }"
            class="btn btn-outline-secondary px-4"
          >
            <i class="fas fa-arrow-left me-2"></i>Retour à la liste
          </router-link>
          <span v-else class="btn btn-outline-secondary px-4 opacity-50">
            <i class="fas fa-arrow-left me-2"></i>Retour à la liste
          </span> -->
          <router-link :to="{ name: 'EditAddBescStep1', params: { id: bescId } }" class="btn btn-danger px-4">
            Retour à l'étape 1
          </router-link>
          <button type="submit" class="btn btn-primary px-5 py-2" :disabled="!peutContinuer || enregistrementEnCours">
            <span v-if="enregistrementEnCours">
              <i class="fas fa-spinner fa-spin me-2"></i>
              Mise à jour...
            </span>
            <span v-else>
              <i class="fas fa-save me-2"></i>
              Mettre à jour & Passer à l'étape 3
            </span>
          </button>
        </div>

      </Form>
    </div>
  </div>

  <!-- Modal pour voir le conteneur -->
  <div v-if="afficherModalVoirConteneurs" class="modal-backdrop fade show" style="z-index: 1070;"></div>
  <div v-if="afficherModalVoirConteneurs" class="modal fade show d-block" tabindex="-1" style="z-index: 1071;">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title text-white">
            <i class="fas fa-container-storage me-2"></i>
            Conteneur de la marchandise {{ (marchandiseSelectionnee?.index ?? 0) + 1 }}
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="fermerModalVoirConteneurs"></button>
        </div>

        <div class="modal-body">
          <!-- Info marchandise -->
          <div class="alert alert-info mb-4">
            <strong>Marchandise :</strong> {{ marchandiseSelectionnee?.data.description }}
            <br>
            <strong>Type :</strong> {{typeConditionnements.find(t => t.value ===
              marchandiseSelectionnee?.data.typeConditionnement)?.label }}
            <br>
            <strong>Poids :</strong> {{ marchandiseSelectionnee?.data.poidsBrut?.toFixed(2) }} tonnes
          </div>

          <!-- Conteneur associé -->
          <h6 class="fw-bold mb-3">
            <i class="fas fa-list me-2"></i>
            Conteneur associé
          </h6>

          <div v-if="(marchandiseSelectionnee?.data as any)?.conteneur">
            <div class="table-responsive">
              <table class="table table-bordered table-sm">
                <thead class="table-light">
                  <tr>
                    <th>N° Conteneur</th>
                    <th>Type</th>
                    <th>Scellé</th>
                    <th>Type chargement</th>
                    <th>Poids brut (Tonnes)</th>
                    <th>Volume (m³)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>{{ (marchandiseSelectionnee?.data as any)?.conteneur?.numeroConteneur }}</strong></td>
                    <td>{{typeConteneurs.find(t => t.value === (marchandiseSelectionnee?.data as
                      any)?.conteneur?.typeConteneur)?.label || '-' }}</td>
                    <td>{{ (marchandiseSelectionnee?.data as any)?.conteneur?.numeroScelle || '-' }}</td>
                    <td>{{typeChargements.find(t => t.value === (marchandiseSelectionnee?.data as
                      any)?.conteneur?.typeChargement)?.label || '-' }}</td>
                    <td class="text-end">{{ (marchandiseSelectionnee?.data as any)?.conteneur?.poidsBrut?.toFixed(2) ||
                      '0.00' }}</td>
                    <td class="text-end">{{ (marchandiseSelectionnee?.data as any)?.conteneur?.volume?.toFixed(2) || '-'
                      }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-else class="alert alert-warning">
            <i class="fas fa-exclamation-triangle me-2"></i>
            Aucun conteneur associé à cette marchandise
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="fermerModalVoirConteneurs">
            Fermer
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="afficherModalConteneur" class="modal-backdrop fade show conteneur-backdrop"></div>
  <div v-if="afficherModalConteneur" class="modal fade show d-block conteneur-modal" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header" style="background-color: #0a59a4;">
          <h5 class="modal-title text-white">
            Ajouter un conteneur – BL {{ blActuel?.numeroBL || 'N/A' }}
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="fermerModalConteneur"></button>
        </div>

        <Form @submit="enregistrerConteneur" :validation-schema="conteneurSchema" v-slot="{ isSubmitting }">
          <div class="modal-body">
            <div class="row g-3">

              <div class="col-md-6">
                <label class="d-block text-black fw-semibold mb-10">
                  N° Conteneur <span class="text-danger">*</span>
                </label>
                <Field name="numeroConteneur" v-model="nouveauConteneur.numeroConteneur" type="text"
                  class="form-control shadow-none fs-md-15 text-black" placeholder="ex: MSCU1234567" />
                <ErrorMessage name="numeroConteneur" class="text-danger small mt-1" />
              </div>

              <div class="col-md-6">
                <label class="d-block text-black fw-semibold mb-10">
                  N° Scellé <span class="text-danger">*</span>
                </label>
                <Field name="numeroScelle" v-model="nouveauConteneur.numeroScelle" type="text"
                  class="form-control shadow-none fs-md-15 text-black" placeholder="ex: 123456789" />
                <ErrorMessage name="numeroScelle" class="text-danger small mt-1" />
              </div>

              <div class="col-md-6">
                <label class="d-block text-black fw-semibold mb-10">
                  Type de conteneur <span class="text-danger">*</span>
                </label>
                <Field name="typeConteneur" v-model="nouveauConteneur.typeConteneur" v-slot="{ field }">
                  <Multiselect v-bind="field" :options="typeConteneurs" :searchable="true" :loading="loadingRefs"
                    placeholder="Sélectionner..." label="label" track-by="value" />
                </Field>
                <ErrorMessage name="typeConteneur" class="text-danger small mt-1" />
              </div>

              <div class="col-md-6">
                <label class="d-block text-black fw-semibold mb-10">
                  Type de chargement <span class="text-danger">*</span>
                </label>
                <Field name="typeChargement" v-model="nouveauConteneur.typeChargement" v-slot="{ field }">
                  <Multiselect v-bind="field" :options="typeChargements" :searchable="true" :loading="loadingRefs"
                    placeholder="Sélectionner..." label="label" track-by="value" />
                </Field>
                <ErrorMessage name="typeChargement" class="text-danger small mt-1" />
              </div>

              <div class="col-md-6">
                <label class="d-block text-black fw-semibold mb-10">
                  Poids brut (Tonnes)
                  <span class="text-muted">(calculé auto)</span>
                </label>

                <div class="input-group">
                  <input type="number" :value="poidsBrutConteneurCalcule.toFixed(2)"
                    class="form-control bg-light fw-bold" readonly disabled />
                  <span class="input-group-text"
                    :class="poidsBrutConteneurCalcule > 0 ? 'bg-success text-white' : 'bg-secondary text-white'">
                    <i class="fas fa-weight-hanging"></i>
                  </span>
                </div>

                <small class="d-block mt-2" :class="poidsBrutConteneurCalcule > 0 ? 'text-success' : 'text-warning'">
                  <i class="fas" :class="poidsBrutConteneurCalcule > 0 ? 'fa-check-circle' : 'fa-info-circle'"></i>
                  <span v-if="poidsBrutConteneurCalcule > 0">
                    Poids calculé: {{ poidsBrutConteneurCalcule.toFixed(2) }} tonnes
                  </span>
                  <span v-else>
                    Le poids sera calculé automatiquement selon la marchandise
                  </span>
                </small>
              </div>

              <div class="col-md-6">
                <label class="d-block text-black fw-semibold mb-10">Volume (m³)</label>
                <Field name="volume" v-model.number="nouveauConteneur.volume" type="number" step="0.01"
                  class="form-control shadow-none fs-md-15 text-black" />
                <ErrorMessage name="volume" class="text-danger small mt-1" />
              </div>

              <div class="col-12">
                <label class="d-block text-black fw-semibold mb-10">Description</label>
                <Field name="description" v-model="nouveauConteneur.description" as="textarea"
                  class="form-control shadow-none fs-md-15 text-black" rows="3"
                  placeholder="Description du conteneur (optionnel)..."></Field>
                <ErrorMessage name="description" class="text-danger small mt-1" />
              </div>

            </div>
          </div>

          <div class="modal-footer d-flex justify-content-between">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="dupliquerConteneur"
                v-model="garderModalConteneurOuvert">
              <label class="form-check-label text-muted small" for="dupliquerConteneur">
                Garder rempli pour ajouter un autre conteneur
              </label>
            </div>

            <div class="d-flex gap-2">
              <button type="button" class="btn btn-outline-danger" @click="fermerModalConteneur">
                Annuler
              </button>
              <button type="submit" class="btn btn-success" :disabled="isSubmitting">
                {{ isSubmitting ? 'Enregistrement...' : 'Enregistrer' }}
              </button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>

  <div v-if="afficherModalMarchandise" class="modal-backdrop fade show marchandise-backdrop"></div>
  <div v-if="afficherModalMarchandise" class="modal fade show d-block marchandise-modal" tabindex="-1">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header" style="background-color: #0a59a4;">
          <h5 class="modal-title text-white">
            {{ indexMarchandiseEnEdition !== null ? 'Modifier' : 'Ajouter' }} une marchandise – BL {{ blActuel?.numeroBL
            || 'N/A' }}
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="fermerModalMarchandise"></button>
        </div>

        <Form @submit="enregistrerMarchandise" :validation-schema="marchandiseSchema" v-slot="{ isSubmitting }">
          <div class="modal-body">
            <div class="row g-3">

              <div class="col-md-4">
                <label class="d-block text-black fw-semibold mb-10">Code HS (SH)</label>
                <Field name="hscode" v-model="nouvelleMarchandise.hscode" v-slot="{ field, setValue }">
                  <Multiselect 
                    v-bind="field" 
                    :options="hsCodes" 
                    :searchable="true" 
                    :loading="loadingRefs"
                    @update:model-value="(val) => onHscodeChange(val, setValue)"
                    placeholder="Rechercher HS" 
                    label="label" 
                    track-by="value" />
                </Field>
                <ErrorMessage name="hscode" class="text-danger small mt-1" />
              </div>

              <div class="col-md-4">
                <label class="d-block text-black fw-semibold mb-10">
                  Nombre de colis <span class="text-danger">*</span>
                </label>
                <Field name="nombreColis" v-model.number="nouvelleMarchandise.nombreColis" type="number" min="1"
                  class="form-control shadow-none fs-md-15 text-black" />
                <ErrorMessage name="nombreColis" class="text-danger small mt-1" />
              </div>

              <div class="col-md-4">
                <label class="d-block text-black fw-semibold mb-10">
                  Type de conditionnement <span class="text-danger">*</span>
                </label>
                <Field name="typeConditionnement" v-model="nouvelleMarchandise.typeConditionnement" v-slot="{ field }">
                  <Multiselect v-bind="field" :options="typeConditionnements" @change="resetConteneursSelonType"
                    :searchable="true" :loading="loadingRefs" placeholder="Rechercher..." label="label"
                    track-by="value" />
                </Field>
                <ErrorMessage name="typeConditionnement" class="text-danger small mt-1" />
              </div>

              <div class="col-md-6" v-if="afficherSelectConteneur">
                <label class="d-block text-black fw-semibold mb-10">
                  Conteneur associé
                </label>
                <div class="d-flex gap-2">
                  <Multiselect v-model="nouvelleMarchandise.conteneurSelectionne"
                    :options="tousLesConteneursDisponiblesDuBL" :searchable="true"
                    placeholder="Choisir un conteneur existant" label="label" track-by="value" class="flex-grow-1"
                    :allow-empty="true" :close-on-select="true" @select="ajouterConteneurExistantAMarchandise" />

                  <button type="button" class="btn btn-primary btn-sm" @click="ajouterConteneurALaMarchandise"
                    title="Ajouter un nouveau conteneur">
                    <i class="flaticon-plus"></i>
                  </button>
                </div>
                <small class="text-muted">
                  Pour FCL/GROUPAGE : associez un conteneur avec le bouton +
                </small>

                <div v-if="nouvelleMarchandise.conteneur" class="alert alert-success mt-2">
                  <strong>Conteneur associé :</strong> {{ nouvelleMarchandise.conteneur.numeroConteneur }}
                  <button type="button" class="btn btn-sm btn-outline-danger float-end"
                    @click="nouvelleMarchandise.conteneur = null">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>

              <div class="col-12" v-else-if="nouvelleMarchandise.typeConditionnement">
                <div class="alert alert-info">
                  <i class="fas fa-info-circle me-2"></i>
                  <strong>{{typeConditionnements.find(t => t.value === nouvelleMarchandise.typeConditionnement)?.label
                    }}</strong>
                  <p class="mb-0 mt-2 small">
                    Les conteneurs ne sont pas nécessaires pour ce type de conditionnement.
                  </p>
                </div>
              </div>
              <div class="col-md-4">
                <label class="d-block text-black fw-semibold mb-10">Tableau IMDG</label>
                <Field name="tableauIMDG" v-model="nouvelleMarchandise.tableauIMDG" v-slot="{ field }">
                  <Multiselect v-bind="field" :options="tableauxIMDG" :searchable="true" :loading="loadingRefs"
                    placeholder="Rechercher..." label="label" track-by="value" />
                </Field>
                <ErrorMessage name="tableauIMDG" class="text-danger small mt-1" />
              </div>
              <div class="col-md-4">
                <label class="d-block text-black fw-semibold mb-10">Catégorie produit</label>
                <Field name="categorieProduit" v-model="nouvelleMarchandise.categorieProduit" v-slot="{ field, setValue }">
                  <Multiselect 
                    v-bind="field" 
                    :options="categoriesProduit" 
                    :searchable="true" 
                    :loading="loadingRefs"
                    @update:model-value="(val) => onCategorieProduitChange(val, setValue)"
                    placeholder="Rechercher..." 
                    label="label" 
                    track-by="value" />
                </Field>
                <ErrorMessage name="categorieProduit" class="text-danger small mt-1" />
              </div>

              <!-- Alertes produits restreints -->
              <div v-if="restrictionAlerts.marchandise && restrictionAlerts.marchandise.length > 0" class="col-12">
                <div v-for="(restriction, rIndex) in restrictionAlerts.marchandise" :key="rIndex" 
                  :class="[
                    'alert py-3 px-4 mb-3',
                    restriction.restriction_type === 'FORBIDDEN' ? 'alert-danger' : 
                    restriction.restriction_type === 'LICENSE_REQUIRED' ? 'alert-warning' : 
                    restriction.restriction_type === 'SPECIAL_TAX' ? 'alert-info' : 
                    'alert-secondary'
                  ]">
                  
                  <!-- En-tête de l'alerte -->
                  <div class="d-flex align-items-center mb-2">
                    <i :class="[
                      'me-2 fs-4',
                      restriction.restriction_type === 'FORBIDDEN' ? 'ph-prohibit text-danger' : 
                      restriction.restriction_type === 'LICENSE_REQUIRED' ? 'ph-warning text-warning' : 
                      restriction.restriction_type === 'SPECIAL_TAX' ? 'ph-info text-info' : 
                      'ph-warning-circle'
                    ]"></i>
                    <h6 class="fw-bold mb-0">
                      <span v-if="restriction.restriction_type === 'FORBIDDEN'">PRODUIT INTERDIT</span>
                      <span v-else-if="restriction.restriction_type === 'LICENSE_REQUIRED'">LICENCE REQUISE</span>
                      <span v-else-if="restriction.restriction_type === 'SPECIAL_TAX'">TAXE SPÉCIALE APPLICABLE</span>
                      <span v-else>QUANTITÉ LIMITÉE</span>
                    </h6>
                  </div>

                  <!-- Description -->
                  <div class="mb-2">
                    <strong>Description:</strong> {{ restriction.description }}
                  </div>

                  <!-- Détails HS Code / Catégorie -->
                  <div class="small mb-2">
                    <span v-if="restriction.hscode">
                      <strong>HS Code:</strong> {{ restriction.hscode.code }} - {{ restriction.hscode.product }}
                    </span>
                    <span v-if="restriction.categorieProduit" class="ms-3">
                      <strong>Catégorie:</strong> {{ restriction.categorieProduit.code }} - {{ restriction.categorieProduit.name }}
                    </span>
                  </div>

                  <!-- Limites quantité/valeur -->
                  <div v-if="restriction.max_quantity || restriction.max_declared_value" class="small mb-2">
                    <span v-if="restriction.max_quantity">
                      <strong>Quantité max:</strong> {{ restriction.max_quantity }} tonnes
                    </span>
                    <span v-if="restriction.max_declared_value" class="ms-3">
                      <strong>Valeur max:</strong> {{ restriction.max_declared_value }} USD
                    </span>
                  </div>

                  <!-- Document requis -->
                  <div v-if="restriction.typeD" class="mb-2">
                    <strong>Document requis:</strong> {{ restriction.typeD.code }} - {{ restriction.typeD.libelle }}
                  </div>

                  <!-- Taxe spéciale -->
                  <div v-if="restriction.special_tax_rate" class="mb-2">
                    <strong>Taux de taxe spéciale:</strong> {{ restriction.special_tax_rate }}%
                  </div>
                </div>
              </div>

              <div class="col-md-4">
                <label class="d-block text-black fw-semibold mb-10">
                  Poids brut (Tonnes) <span class="text-danger">*</span>
                </label>
                <Field name="poidsBrut" v-model.number="nouvelleMarchandise.poidsBrut" type="number" step="0.01"
                  class="form-control shadow-none fs-md-15 text-black" />
                <ErrorMessage name="poidsBrut" class="text-danger small mt-1" />
              </div>
              <div class="col-md-4">
                <label class="d-block text-black fw-semibold mb-10">
                  Poids net (Tonnes) <span class="text-danger">*</span>
                </label>
                <Field name="poidsNet" v-model.number="nouvelleMarchandise.poidsNet" type="number" step="0.01"
                  class="form-control shadow-none fs-md-15 text-black" />
                <ErrorMessage name="poidsNet" class="text-danger small mt-1" />
              </div>
              <div class="col-md-4">
                <label class="d-block text-black fw-semibold mb-10">Volume (m³)</label>
                <Field name="volume" v-model.number="nouvelleMarchandise.volume" type="number" step="0.01"
                  class="form-control shadow-none fs-md-15 text-black" />
                <ErrorMessage name="volume" class="text-danger small mt-1" />
              </div>

              <div class="col-md-4">
                <label class="d-block text-black fw-semibold mb-10">
                  Valeur déclarée <span class="text-danger">*</span>
                </label>
                <Field name="valeurDeclaree" v-model.number="nouvelleMarchandise.valeurDeclaree" type="number"
                  step="0.01" class="form-control shadow-none fs-md-15 text-black" />
                <ErrorMessage name="valeurDeclaree" class="text-danger small mt-1" />
              </div>

              <div class="col-md-6">
                <label class="d-block text-black fw-semibold mb-10">
                  Devise <span class="text-danger">*</span>
                </label>
                <Field name="devise" v-model="nouvelleMarchandise.devise" v-slot="{ field }">
                  <Multiselect v-bind="field" :options="deviseOptions" :searchable="true" label="label" track-by="value"
                    valueProp="value" placeholder="Sélectionner..." />
                </Field>
                <ErrorMessage name="devise" class="text-danger small mt-1" />
              </div>

              <div class="col-md-6">
                <label class="d-block text-black fw-semibold mb-10">Pays d'origine</label>
                <Field name="paysOrigine" v-model="nouvelleMarchandise.paysOrigine" v-slot="{ field, setValue }">
                  <Multiselect 
                    v-bind="field" 
                    :options="pays" 
                    :searchable="true" 
                    :loading="loadingRefs"
                    placeholder="Rechercher..." 
                    label="label" 
                    track-by="value"
                    @update:model-value="async (val) => {
                      setValue(val);
                      const paysObj = pays.find(p => p.value === val);
                      if (paysObj?.iso2_code) {
                        const result = await checkBlacklist('COUNTRY', paysObj.iso2_code);
                        blacklistAlerts.paysOrigineMarchandise = result.isBlacklisted ? result.details : null;
                      } else {
                        blacklistAlerts.paysOrigineMarchandise = null;
                      }
                    }" />
                </Field>
                <ErrorMessage name="paysOrigine" class="text-danger small mt-1" />
                
                <!-- Alerte blacklist pour pays d'origine -->
                <div v-if="blacklistAlerts.paysOrigineMarchandise" class="alert alert-danger mt-2 py-2 px-3">
                  <small>
                    <strong>⚠️ ALERTE BLACKLIST:</strong> Liste {{ blacklistAlerts.paysOrigineMarchandise.list_code }} - {{ blacklistAlerts.paysOrigineMarchandise.reason }}
                  </small>
                </div>
              </div>

              <div class="col-12">
                <label class="d-block text-black fw-semibold mb-10">
                  Description complète <span class="text-danger">*</span>
                </label>
                <Field name="description" v-model="nouvelleMarchandise.description" as="textarea" rows="2"
                  class="form-control form-control-sm shadow-none fs-md-15 text-black"
                  placeholder="ex: 2 véhicules Toyota Camry 2024, neufs, couleur blanche..." />

                <ErrorMessage name="description" class="text-danger small mt-1" />
              </div>
            </div>
          </div>

          <div class="modal-footer d-flex justify-content-between">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="dupliquerMarchandise" v-model="garderModalOuvert">
              <label class="form-check-label text-muted small" for="dupliquerMarchandise">
                Garder le formulaire rempli pour ajouter une autre marchandise
              </label>
            </div>

            <div class="d-flex gap-2">
              <button type="button" class="btn btn-outline-danger" @click="fermerModalMarchandise">
                Annuler
              </button>
              <button type="submit" class="btn btn-success" :disabled="isSubmitting">
                {{ isSubmitting ? 'Enregistrement...' : (indexMarchandiseEnEdition !== null ? 'Modifier' :
                'Enregistrer') }}
              </button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>

  <!-- Modal pour afficher les marchandises d'un conteneur -->
  <div v-if="afficherModalMarchandisesConteneur" class="modal-backdrop fade show" style="z-index: 1040;"></div>
  <div v-if="afficherModalMarchandisesConteneur" class="modal fade show d-block" tabindex="-1" style="z-index: 1041;">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title text-white">
            <i class="fas fa-container-storage me-2"></i>
            Marchandises du conteneur {{ conteneurSelectionne?.numeroConteneur }}
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="fermerModalMarchandisesConteneur"></button>
        </div>

        <div class="modal-body">
          <!-- Info conteneur -->
          <div class="alert alert-info mb-4" v-if="conteneurSelectionne">
            <div class="row">
              <div class="col-md-3">
                <strong>Conteneur :</strong> {{ conteneurSelectionne.numeroConteneur }}
              </div>
              <div class="col-md-3">
                <strong>Type :</strong> {{ typeConteneurs.find(t => t.value === conteneurSelectionne.typeConteneur)?.label || 'N/A' }}
              </div>
              <div class="col-md-3">
                <strong>Scellé :</strong> {{ conteneurSelectionne.numeroScelle || 'N/A' }}
              </div>
              <div class="col-md-3">
                <strong>Poids :</strong> {{ conteneurSelectionne.poidsBrut?.toFixed(2) || '0.00' }} tonnes
              </div>
            </div>
          </div>

          <!-- Liste des marchandises -->
          <h6 class="fw-bold mb-3">
            <i class="fas fa-boxes me-2"></i>
            Marchandises ({{ marchandisesConteneurSelectionne.length }})
          </h6>

          <div class="table-responsive">
            <table class="table table-hover table-bordered">
              <thead class="table-light">
                <tr>
                  <th>N°</th>
                  <th>Description</th>
                  <th>Code HS</th>
                  <th>Type Conditionnement</th>
                  <th>Poids (Tonnes)</th>
                  <th>Poids Net (Tonnes)</th>
                  <th>Volume (m³)</th>
                  <th>Valeur</th>
                  <th>Devise</th>
                  <th>Catégorie</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(marchandise, index) in marchandisesConteneurSelectionne" :key="index">
                  <td>{{ marchandise.originalIndex + 1 }}</td>
                  <td>
                    <div class="fw-semibold">{{ marchandise.description }}</div>
                  </td>
                  <td>
                    <small class="text-muted">
                      {{
                        typeof marchandise.hscode === 'object'
                          ? (marchandise.hscode?.label || `${marchandise.hscode?.code} - ${marchandise.hscode?.product}`)
                          : hsCodes.find(h => h.value === marchandise.hscode)?.label || '-'
                      }}
                    </small>
                  </td>
                  <td>
                    {{ marchandise.nombreColis }} {{ typeConditionnements.find(t => t.value === marchandise.typeConditionnement)?.label || '-' }}
                  </td>
                  <td class="text-end">{{ marchandise.poidsBrut?.toFixed(2) || '0.00' }}</td>
                  <td class="text-end">{{ marchandise.poidsNet?.toFixed(2) || '-' }}</td>
                  <td class="text-end">{{ marchandise.volume?.toFixed(2) || '-' }}</td>
                  <td class="text-end">{{ marchandise.valeurDeclaree?.toFixed(2) || '0.00' }}</td>
                  <td>{{ marchandise.devise || 'USD' }}</td>
                  <td>
                    <small class="text-muted">
                      {{
                        typeof marchandise.categorieProduit === 'object'
                          ? ((marchandise.categorieProduit as any)?.label || `${(marchandise.categorieProduit as any)?.code} - ${(marchandise.categorieProduit as any)?.name}`)
                          : categoriesProduit.find(c => c.value === marchandise.categorieProduit)?.label || '-'
                      }}
                    </small>
                  </td>
                  <td>
                    <button 
                      type="button" 
                      class="btn btn-outline-primary btn-sm me-1" 
                      @click="modifierMarchandise(blIndex, marchandise.originalIndex); fermerModalMarchandisesConteneur()"
                      title="Modifier cette marchandise">
                      <i class="flaticon-edit"></i>
                    </button>
                    <button 
                      type="button" 
                      class="btn btn-outline-danger btn-sm"
                      @click="supprimerMarchandise(blIndex, marchandise.originalIndex)" 
                      title="Supprimer cette marchandise">
                      <i class="flaticon-delete"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
              <tfoot class="table-light">
                <tr>
                  <td colspan="4" class="text-end fw-bold">Total:</td>
                  <td class="text-end fw-bold">
                    {{ marchandisesConteneurSelectionne.reduce((sum, m) => sum + (m.poidsBrut || 0), 0).toFixed(2) }} tonnes
                  </td>
                  <td colspan="4" class="text-end fw-bold">
                    {{ marchandisesConteneurSelectionne.reduce((sum, m) => sum + (m.valeurDeclaree || 0), 0).toFixed(2) }} USD
                  </td>
                  <td colspan="2" class="text-center fw-bold">
                    {{ marchandisesConteneurSelectionne.length }} marchandise(s)
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="fermerModalMarchandisesConteneur">
            Fermer
          </button>
        </div>
      </div>
    </div>
  </div>

</template>

<script lang="ts">
import { ref, computed, onMounted, defineComponent, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Form, Field, ErrorMessage } from 'vee-validate'
import Multiselect from '@vueform/multiselect'
import ApiService from '@/services/ApiService'
import { success, error, monSwal, warning } from '@/utils/utils'
import * as Yup from 'yup'
import { BescLigne } from '@/models/BescLigne'
import { Conteneur } from '@/models/Conteneur'
import { Bl } from '@/models/Bl'
import { bescWizard } from '@/utils/bescWizard'
// Supprimé: import { bescWizard, loadBescProgressionFromAPI } from '@/utils/bescWizard'

export default defineComponent({
  name: 'EditAddBescStep2',
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },
  setup() {

    const router = useRouter()
    const route = useRoute()

    const idBesc = ref<string>('')
    const chargementDonnees = ref(false)
    const blsExistantsEnBase = ref(false)
    const bescId = route.params.id as string
    const chargementCompagnies = ref(false)
    const chargementNavires = ref(false)
    const chargement = ref(false)
    const enregistrementEnCours = ref(false)
    
    
    const corrections = ref<any[]>([])

      const fetchRequestCorrection = async () => {
                try {
                    const { data } = await ApiService.get(`/corrections/besc/${bescId}`)
                    console.log("dattaaaa",data);
                    
                    corrections.value = data.data.filter((cor: any) => cor.status === 'OPEN')
                    
                } catch(err: any) {
                    error(err.response?.data?.message || 'Erreur de récupération')
                }
            }

       const markCorrectionAsFixed = async (correctionId: string) => {
                try {
                    await ApiService.patch(`/correction/${correctionId}/status`, { 
                        status: 'RESOLVED' 
                    })
                    
                    corrections.value = corrections.value.filter(c => c.id !== correctionId)
                    success('Correction marquée comme résolue !')
                    
                } catch (err: any) {
                    error('Erreur lors de la mise à jour')
                }
            }


    onMounted(async () => {
      await fetchRequestCorrection()
    })

    const listeNavires = ref<Array<{ label: string; value: number; imo?: string }>>([])
    const compagniesMaritimes = ref<Array<{ label: string; value: number }>>([])
    const hsCodes = ref<any[]>([])
    const pays = ref<any[]>([])
    const loadingRefs = ref(false)
    const typeConteneurs = ref<any[]>([])
    const typeChargements = ref<any[]>([])
    const blForm = ref(null)
    const listeBLs = ref<Bl[]>([])  

    // Alertes blacklist
    const blacklistAlerts = ref<{
      navires: Record<number, any>;
      paysOrigineMarchandise: any;
    }>({
      navires: {},
      paysOrigineMarchandise: null
    })

    // Alertes produits restreints
    const restrictionAlerts = ref<{
      marchandise: any;
    }>({
      marchandise: null
    })  

    const afficherModalMarchandise = ref(false)
    const afficherModalConteneur = ref(false)
    const indexBLCourant = ref<number | null>(null)
    const indexMarchandiseEnEdition = ref<number | null>(null)

    const deviseOptions = [
      { value: 'USD', label: 'USD - Dollar américain' },
      { value: 'EUR', label: 'EUR - Euro' },
      { value: 'XOF', label: 'XOF - Franc CFA' }
    ]

    const typeConditionnements = ref([
      { value: 'FCL', label: 'FCL' },
      { value: 'ROULANT', label: 'Roulant' },
      { value: 'GROUPAGE', label: 'Groupage' },
      { value: 'CONVENTIONNEL', label: 'Risque Groupage' },
      { value: 'VRAC', label: 'Vrac navire complet' }
    ])

    const tableauxIMDG = ref<any[]>([])
    const categoriesProduit = ref<any[]>([])
    const garderModalOuvert = ref(false)
    const garderModalConteneurOuvert = ref(false)
    let temporisateurRecherche: ReturnType<typeof setTimeout> | null = null
    const indexConteneurEnEdition = ref<number | null>(null)
    const isEditMode = computed(() => blsExistantsEnBase.value)

    const blSchema = Yup.object({
      listeBLs: Yup.array().of(
        Yup.object({
          numeroBL: Yup.string().required('Le numéro BL est obligatoire'),
          idCompagnie: Yup.string().required('La compagnie est obligatoire'),
          poidsBrut: Yup.number().moreThan(0, 'Le poidsBrut doit être > 0'),
          idNavire: Yup.string().required('Le navire est obligatoire'),
          referenceVoyage: Yup.string().required('La referenceVoyage est obligatoire'),
        })
      )
    })

    const conteneurSchema = Yup.object({
      numeroConteneur: Yup.string()
        .required('Le numéro de conteneur est obligatoire')
        .test('format-conteneur', 'Format invalide (ex: MSCU1234567)', (value) => {
          if (!value) return false
          const cleaned = value.replace(/[\s-]/g, '').toUpperCase()
          return /^[A-Z]{4}[0-9]{7}$/.test(cleaned)
        }),
      numeroScelle: Yup.string()
        .required('Le numéro de scellé est obligatoire')
        .min(3, 'Le numéro de scellé doit faire au moins 3 caractères'),
      typeConteneur: Yup.string().required('Le type de conteneur est obligatoire'),
      typeChargement: Yup.string().required('Le type de chargement est obligatoire'),
      volume: Yup.number().nullable().min(0, 'Le volume doit être positif ou zéro'),
      description: Yup.string().nullable(),
    })

    const marchandiseSchema = Yup.object({
      description: Yup.string()
        .required('La description complète est obligatoire')
        .min(5, 'La description doit faire au moins 5 caractères'),
      nombreColis: Yup.number()
        .required('Le nombre de colis est obligatoire')
        .min(1, 'Au moins 1 colis'),
      typeConditionnement: Yup.string().required('Le type de conditionnement est obligatoire'),
      poidsBrut: Yup.number()
        .required('Le poids brut est obligatoire')
        .min(0.01, 'Le poids brut doit être supérieur à 0'),
      poidsNet: Yup.number()
        .required('Le poids net est obligatoire')
        .min(0.01, 'Le poids net doit être supérieur à 0')
        .test('poidsNet <= poidsBrut', 'Le poids net ne peut pas dépasser le poids brut',
          function (value) {
            return value <= this.parent.poidsBrut
          }),
      valeurDeclaree: Yup.number()
        .required('La valeur déclarée est obligatoire')
        .min(0.01, 'La valeur doit être supérieure à 0'),
      devise: Yup.string().required('La devise est obligatoire'),
      volume: Yup.number().nullable().min(0, 'Le volume doit être positif'),
      hscode: Yup.mixed().nullable(),
      paysOrigine: Yup.mixed().nullable(),
      tableauIMDG: Yup.mixed().nullable(),
      categorieProduit: Yup.mixed().nullable(),
    })

    const nouvelleMarchandise = ref<any>({
      description: '',
      hscode: null,
      nombreColis: 1,
      typeConditionnement: null,
      poidsBrut: 0,
      poidsNet: 0,
      volume: null,
      valeurDeclaree: 0,
      devise: 'USD',
      paysOrigine: null,
      tableauIMDG: null,
      categorieProduit: null,
      conteneur: null,
      conteneurSelectionne: null
    })

    const nouveauConteneur = ref<Conteneur>({
      numeroConteneur: '',
      typeConteneur: '',
      numeroScelle: '',
      typeChargement: '',
      poidsBrut: 0,
      volume: null,
      description: ''
    })

    const afficherSelectConteneur = computed(() => {
      const type = nouvelleMarchandise.value.typeConditionnement
      return type === 'FCL' || type === 'GROUPAGE'
    })

    const tousLesConteneursDisponiblesDuBL = computed(() => {
      if (indexBLCourant.value === null) return []

      const bl = listeBLs.value[indexBLCourant.value]
      const conteneursUniques = new Map()

      bl.marchandises?.forEach((marchandise: any) => {
        if (marchandise.conteneur?.numeroConteneur) {
          const key = marchandise.conteneur.numeroConteneur
          if (!conteneursUniques.has(key)) {
            conteneursUniques.set(key, marchandise.conteneur)
          }
        }
      })

      if (nouvelleMarchandise.value.conteneur?.numeroConteneur) {
        const key = nouvelleMarchandise.value.conteneur.numeroConteneur
        if (!conteneursUniques.has(key)) {
          conteneursUniques.set(key, nouvelleMarchandise.value.conteneur)
        }
      }

      return Array.from(conteneursUniques.values()).map((c: any) => ({
        label: `${c.numeroConteneur} - ${typeConteneurs.value.find(t => t.value === c.typeConteneur)?.label || 'N/A'} (Scellé: ${c.numeroScelle || '-'})`,
        value: c.numeroConteneur,
        conteneur: c
      }))
    })

    const ajouterConteneurExistantAMarchandise = (option: any) => {
      if (!option || !option.conteneur) return

      nouvelleMarchandise.value.conteneur = { ...option.conteneur }
      success('Conteneur associé à la marchandise')

      nouvelleMarchandise.value.conteneurSelectionne = null
    }

    const resetConteneursSelonType = (nouveauType: string) => {
      if (nouveauType !== 'FCL' && nouveauType !== 'GROUPAGE') {
        nouvelleMarchandise.value.conteneur = null
        nouvelleMarchandise.value.conteneurSelectionne = null
      }
    }

    const fermerModalConteneur = () => {
      afficherModalConteneur.value = false
      indexConteneurEnEdition.value = null
      nouveauConteneur.value = {
        numeroConteneur: '',
        typeConteneur: '',
        typeChargement: '',
        numeroScelle: '',
        poidsBrut: 0,
        volume: null,
        description: ''
      }
    }

    const fermerModalMarchandise = () => {
      afficherModalMarchandise.value = false
      indexMarchandiseEnEdition.value = null
      nouvelleMarchandise.value = {
        description: '',
        hscode: null,
        nombreColis: 1,
        typeConditionnement: null,
        poidsBrut: 0,
        poidsNet: 0,
        volume: null,
        valeurDeclaree: 0,
        devise: 'USD',
        paysOrigine: null,
        tableauIMDG: null,
        categorieProduit: null,
        conteneur: null,
        conteneurSelectionne: null
      }
    }

    const afficherModalVoirConteneurs = ref(false)
    const marchandiseSelectionnee = ref<{
      blIndex: number
      index: number
      data: any
    } | null>(null)

    const afficherConteneursModal = (blIndex: number, marchandiseIndex: number) => {
      const bl = listeBLs.value[blIndex]
      const marchandise = bl.marchandises[marchandiseIndex] as any

      marchandiseSelectionnee.value = {
        blIndex,
        index: marchandiseIndex,
        data: {
          ...marchandise,
          conteneur: marchandise.conteneur || null
        }
      }

      afficherModalVoirConteneurs.value = true
    }

    const fermerModalVoirConteneurs = () => {
      afficherModalVoirConteneurs.value = false
      marchandiseSelectionnee.value = null
    }

    const modifierMarchandise = (blIndex: number, marchandiseIndex: number) => {
      const bl = listeBLs.value[blIndex]
      const marchandise = bl.marchandises[marchandiseIndex] as any

      indexBLCourant.value = blIndex
      indexMarchandiseEnEdition.value = marchandiseIndex

      nouvelleMarchandise.value = {
        id: marchandise.id,
        description: marchandise.description,
        hscode: typeof marchandise.hscode === 'object' ? marchandise.hscode.id : marchandise.hscode,
        nombreColis: marchandise.nombreColis,
        typeConditionnement: marchandise.typeConditionnement,
        poidsBrut: marchandise.poidsBrut,
        poidsNet: marchandise.poidsNet,
        volume: marchandise.volume,
        valeurDeclaree: marchandise.valeurDeclaree,
        devise: marchandise.devise,
        paysOrigine: typeof marchandise.paysOrigine === 'object' ? marchandise.paysOrigine.id : marchandise.paysOrigine,
        tableauIMDG: typeof marchandise.tableauIMDG === 'object' ? marchandise.tableauIMDG.id : marchandise.tableauIMDG,
        categorieProduit: typeof marchandise.categorieProduit === 'object' ? marchandise.categorieProduit.id : marchandise.categorieProduit,
        conteneur: marchandise.conteneur ? { ...marchandise.conteneur } : null,
        conteneurSelectionne: marchandise.conteneur?.numeroConteneur || null
      }

      afficherModalMarchandise.value = true
    }

    const ajouterConteneurALaMarchandise = () => {
      nouveauConteneur.value = {
        numeroConteneur: '',
        typeConteneur: '',
        typeChargement: '',
        numeroScelle: '',
        poidsBrut: 0,
        volume: null,
        description: ''
      }
      indexConteneurEnEdition.value = null
      afficherModalConteneur.value = true
    }

    const calculerConteneurUniques = (bl: any) => {
      const conteneurUniques = new Set()
      bl.marchandises?.forEach((marchandise: any) => {
        if (marchandise.conteneur?.numeroConteneur) {
          conteneurUniques.add(marchandise.conteneur.numeroConteneur)
        }
      })
      return conteneurUniques.size
    }

    // Fonction pour obtenir la liste des conteneurs uniques avec leurs marchandises
    const obtenirConteneursUniques = (bl: any) => {
      if (!bl.marchandises || bl.marchandises.length === 0) {
        return []
      }

      const conteneursMap = new Map()

      bl.marchandises.forEach((marchandise: any, index: number) => {
        if ((marchandise as any).conteneur?.numeroConteneur) {
          const numeroConteneur = (marchandise as any).conteneur.numeroConteneur
          
          if (!conteneursMap.has(numeroConteneur)) {
            conteneursMap.set(numeroConteneur, {
              ...(marchandise as any).conteneur,
              marchandises: []
            })
          }

          conteneursMap.get(numeroConteneur).marchandises.push({
            ...marchandise,
            originalIndex: index
          })
        }
      })

      return Array.from(conteneursMap.values())
    }

    // Fonction pour obtenir les marchandises sans conteneur (vrac)
    const obtenirMarchandisesVrac = (bl: any) => {
      if (!bl.marchandises || bl.marchandises.length === 0) {
        return []
      }

      return bl.marchandises
        .map((marchandise: any, index: number) => ({
          ...marchandise,
          originalIndex: index
        }))
        .filter((marchandise: any) => !(marchandise as any).conteneur)
    }

    // Modal pour afficher les marchandises d'un conteneur
    const afficherModalMarchandisesConteneur = ref(false)
    const conteneurSelectionne = ref<any>(null)
    const marchandisesConteneurSelectionne = ref<any[]>([])
    const blIndex = ref<number>(0)

    const afficherMarchandisesConteneur = (blIndexParam: number, numeroConteneur: string) => {
      blIndex.value = blIndexParam
      const bl = listeBLs.value[blIndexParam]
      const marchandises = bl.marchandises.filter((m: any) => 
        (m as any).conteneur?.numeroConteneur === numeroConteneur
      )
      
      conteneurSelectionne.value = (marchandises[0] as any)?.conteneur
      marchandisesConteneurSelectionne.value = marchandises.map((m: any, index: number) => ({
        ...m,
        originalIndex: bl.marchandises.indexOf(m)
      }))
      
      afficherModalMarchandisesConteneur.value = true
    }

    const fermerModalMarchandisesConteneur = () => {
      afficherModalMarchandisesConteneur.value = false
      conteneurSelectionne.value = null
      marchandisesConteneurSelectionne.value = []
    }

    // Fonction pour calculer le poids brut total d'un BL à partir de ses marchandises
    const calculerPoidsBrutBL = (bl: any) => {
      if (!bl.marchandises || bl.marchandises.length === 0) {
        return 0
      }
      
      return bl.marchandises.reduce((total: number, marchandise: any) => {
        return total + (parseFloat(marchandise.poidsBrut) || 0)
      }, 0)
    }

    // const poidsTotalBrut = computed(() =>
    //   listeBLs.value.reduce((t, bl) => t + (Number(bl.poidsBrut) || 0), 0)
    // )
      const poidsTotalBrut = computed(() =>
      listeBLs.value.reduce((totalBLs, bl) => {
        const poidsMarchandises = bl.marchandises?.reduce((totalMarchandises, marchandise) =>
          totalMarchandises + (Number(marchandise.poidsBrut) || 0), 0) || 0;
        return totalBLs + poidsMarchandises;
      }, 0)
    )

    const nombreCompagniesUniques = computed(() => {
      const ids = listeBLs.value.filter(b => b.idCompagnie).map(b => b.idCompagnie!)
      return new Set(ids).size
    })

    const nombreBLValides = computed(() => {
      return listeBLs.value.filter(bl => {
        return (
          bl.numeroBL?.trim() &&
          bl.idCompagnie &&
          bl.idNavire &&
          bl.referenceVoyage?.trim() &&
          bl.poidsBrut > 0
        )
      }).length
    })

    const peutContinuer = computed(() => listeBLs.value.length > 0 && nombreBLValides.value === listeBLs.value.length)

     // Nouvelle propriété computed pour vérifier si on peut ajouter un BL
    const peutAjouterBL = computed(() => {
      // Si aucun BL n'existe, on peut toujours ajouter le premier
      if (listeBLs.value.length === 0) return true

      // Si des BLs existent, vérifier qu'au moins un a des informations saisies
      return listeBLs.value.some(bl => {
        return bl.numeroBL?.trim() ||
          bl.idCompagnie ||
          bl.idNavire ||
          bl.referenceVoyage?.trim() ||
          (bl.marchandises && bl.marchandises.length > 0)
      })
    })
    const chargerCompagnieOption = async (idCompagnie: number) => {
      try {
        const { data } = await ApiService.get(`/compagnies/${idCompagnie}`)
        const compagnie = data.data
        const option = {
          label: `${compagnie.name} ${compagnie.scac_code ? `- ${compagnie.scac_code}` : ''}`,
          value: compagnie.id
        }
        if (!compagniesMaritimes.value.find(c => c.value === compagnie.id)) {
          compagniesMaritimes.value.push(option)
        }
      } catch (err) {
        console.error('Erreur chargement compagnie:', err)
      }
    }

    const chargerNavireOption = async (idNavire: number) => {
      try {
        const { data } = await ApiService.get(`/vessels/${idNavire}`)
        const navire = data.data
        const option = {
          label: `${navire.name} - MMSI: ${navire.mmsi} - IMO: ${navire.imo}`,
          value: navire.id,
          imo: navire.imo
        }
        if (!listeNavires.value.find(n => n.value === navire.id)) {
          listeNavires.value.push(option)
        }
      } catch (err) {
        console.error('Erreur chargement navire:', err)
      }
    }

    const fetchCodeSH = async () => {
      try {
        const { data } = await ApiService.get("/hscodes")
        hsCodes.value = data.data.data.map((hs: any) => ({
          label: `${hs.code} - ${hs.product}`,
          value: hs.id
        }))
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur libelle')
      }
    }

    const fetchPays = async () => {
      try {
        const { data } = await ApiService.get("/pays")
        pays.value = data.data.data.map((p: any) => ({
          label: `${p.name_fr}`,
          value: p.id,
          iso2_code: p.iso2_code
        }))
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur recuperation pays')
      }
    }

    const fetchTypeConteneur = async () => {
      try {
        const { data } = await ApiService.get("/type-containers")
        typeConteneurs.value = data.data.data.map((type: any) => ({
          label: `${type.libelle}`,
          value: type.id
        }))
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur type conteneurs')
      }
    }

    const fetchChargement = async () => {
      try {
        const { data } = await ApiService.get("/type-chargements")
        typeChargements.value = data.data.data.map((type: any) => ({
          label: `${type.code} - ${type.libelle}`,
          value: type.id
        }))
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur type chargements')
      }
    }

    const fetchTableauxIMDG = async () => {
      try {
        const { data } = await ApiService.get("/tableauIMDG")
        tableauxIMDG.value = data.data.data.map((tableau: any) => ({
          label: `${tableau.classeIMDG}`,
          value: tableau.id
        }))
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur tableaux IMDG')
      }
    }

    const fetchCategoriesProduit = async () => {
      try {
        const { data } = await ApiService.get("/categProduits")
        categoriesProduit.value = data.data.data.map((categorie: any) => ({
          label: `${categorie.code} - ${categorie.name}`,
          value: categorie.id
        }))
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur catégories produit')
      }
    }

    const ajouterBL = () => {
      listeBLs.value.push({
        id: null,
        numeroBL: '',
        idCompagnie: null,
        idNavire: null,
        referenceVoyage: '',
        poidsBrut: 0,
        marchandises: [],
        erreurs: {}
      })
    }

    const supprimerBL = (index: number) => {
      if (listeBLs.value.length === 1) {
        warning('Un BESC doit contenir au moins un BL')
        return
      }
      listeBLs.value.splice(index, 1)
    }

    const validerBL = (index: number): boolean => {
      const bl = listeBLs.value[index]
      bl.erreurs = {}

      if (!bl.numeroBL?.trim())
        bl.erreurs.numeroBL = 'Le numéro de BL est obligatoire'
      if (!bl.idCompagnie)
        bl.erreurs.idCompagnie = 'La compagnie maritime est requise'
      if (!bl.idNavire)
        bl.erreurs.idNavire = 'Le navire est obligatoire'
      if (!bl.referenceVoyage?.trim())
        bl.erreurs.referenceVoyage = 'La référence de voyage est obligatoire'
      if (!bl.poidsBrut || bl.poidsBrut <= 0)
        bl.erreurs.poidsBrut = 'Le poids brut doit être > 0'

      if (!bl.marchandises || bl.marchandises.length === 0) {
        bl.erreurs.marchandises = 'Au moins une marchandise est requise'
      }

      return Object.keys(bl.erreurs).length === 0
    }

    const toutValider = (): boolean => listeBLs.value.every((_, i) => validerBL(i))

    const rechercherCompagnie = async (query: string) => {
      if (temporisateurRecherche) clearTimeout(temporisateurRecherche)
      if (!query || query.length < 2) { compagniesMaritimes.value = []; return }

      temporisateurRecherche = setTimeout(async () => {
        chargementCompagnies.value = true
        try {
          const { data } = await ApiService.get(`/compagnies?search=${encodeURIComponent(query)}`)
          compagniesMaritimes.value = data.data.map((c: any) => ({
            label: `${c.name} ${c.scac_code ? `- ${c.scac_code}` : ''}`,
            value: c.id
          }))
        } catch (err) {
          error('Erreur recherche compagnies')
          compagniesMaritimes.value = []
        } finally {
          chargementCompagnies.value = false
        }
      }, 300)
    }

    const rechercherNavire = async (query: string) => {
      if (temporisateurRecherche) clearTimeout(temporisateurRecherche)
      if (!query || query.length < 2) { listeNavires.value = []; return }

      temporisateurRecherche = setTimeout(async () => {
        chargementNavires.value = true
        try {
          const { data } = await ApiService.get(`/vessels?search=${encodeURIComponent(query)}`)
          listeNavires.value = data.data.map((n: any) => ({
            label: `${n.name} - MMSI: ${n.mmsi} - IMO: ${n.imo}`,
            value: n.id,
            imo: n.imo
          }))
        } catch (err) {
          error('Erreur recherche navires')
          listeNavires.value = []
        } finally {
          chargementNavires.value = false
        }
      }, 300)
    }

    const ouvrirModalMarchandise = (index: number) => {
      indexBLCourant.value = index
      indexMarchandiseEnEdition.value = null

      const conteneursDisponibles = tousLesConteneursDisponiblesDuBL.value
      const premierConteneur = conteneursDisponibles.length > 0 ? conteneursDisponibles[0].value : null

      nouvelleMarchandise.value = {
        description: '',
        hscode: null,
        nombreColis: 1,
        typeConditionnement: null,
        poidsBrut: 0,
        poidsNet: 0,
        volume: null,
        valeurDeclaree: 0,
        devise: 'USD',
        paysOrigine: null,
        tableauIMDG: null,
        categorieProduit: null,
        conteneur: null,
        conteneurSelectionne: premierConteneur
      }

      afficherModalMarchandise.value = true
    }

    const enregistrerMarchandise = () => {
      if (indexBLCourant.value === null) return

      if (restrictionAlerts.value.marchandise && restrictionAlerts.value.marchandise.length > 0) {
        const hasForbidden = restrictionAlerts.value.marchandise.some(
          (r: any) => r.restriction_type === 'FORBIDDEN'
        );
        
        if (hasForbidden) {
          error('Cette marchandise est interdite et ne peut pas être ajoutée au BESC');
          return;
        }

        // 2. Bloquer si limites QUOTA dépassées
        const quotaLimited = restrictionAlerts.value.marchandise.filter(
          (r: any) => r.restriction_type === 'QUOTA_LIMITED'
        );
        
        for (const restriction of quotaLimited) {
          if (restriction.max_quantity && nouvelleMarchandise.value.poidsBrut > restriction.max_quantity) {
            error(`Quantité maximale autorisée: ${restriction.max_quantity} tonnes`);
            return;
          }
          if (restriction.max_declared_value && nouvelleMarchandise.value.valeurDeclaree > restriction.max_declared_value) {
            error(`Valeur maximale autorisée: ${restriction.max_declared_value} USD`);
            return;
          }
        }

        // 3. Avertir pour LICENSE_REQUIRED (ne pas bloquer)
        const licenseRequired = restrictionAlerts.value.marchandise.filter(
          (r: any) => r.restriction_type === 'LICENSE_REQUIRED'
        );
        
        if (licenseRequired.length > 0) {
          warning(' Cette marchandise nécessite une licence. Pensez à uploader le(s) document(s) requis à l\'étape 3.');
        }
      }

      const bl = listeBLs.value[indexBLCourant.value]
      const marchandise = {
        ...nouvelleMarchandise.value,
        restrictions: restrictionAlerts.value.marchandise || [],
        hasLicenseRequired: restrictionAlerts.value.marchandise ? 
          restrictionAlerts.value.marchandise.some((r: any) => r.restriction_type === 'LICENSE_REQUIRED') : false,
        hasSpecialTax: restrictionAlerts.value.marchandise ? 
          restrictionAlerts.value.marchandise.some((r: any) => r.restriction_type === 'SPECIAL_TAX') : false
      }

      if (marchandise.conteneur) {
        let poidsBrutConteneur = marchandise.poidsBrut || 0

        bl.marchandises?.forEach((m: any) => {
          if (m.conteneur?.numeroConteneur === marchandise.conteneur.numeroConteneur &&
            m.id !== marchandise.id) {
            poidsBrutConteneur += m.poidsBrut || 0
          }
        })

        marchandise.conteneur.poidsBrut = poidsBrutConteneur
      }

      if (indexMarchandiseEnEdition.value !== null) {
        bl.marchandises[indexMarchandiseEnEdition.value] = {
          ...marchandise,
          id: bl.marchandises[indexMarchandiseEnEdition.value].id
        }
        success('Marchandise modifiée avec succès')
        indexMarchandiseEnEdition.value = null
      } else {
        bl.marchandises.push(marchandise)
        success('Marchandise ajoutée avec succès')
      }

      // Réinitialiser les alertes
      restrictionAlerts.value.marchandise = null;

      if (garderModalOuvert.value) {
        nouvelleMarchandise.value = {
          ...marchandise,
          description: '',
          nombreColis: 1,
          poidsBrut: 0,
          poidsNet: 0,
          volume: null,
          valeurDeclaree: 0,
          conteneur: null,
          restrictions: [],
          hasLicenseRequired: false,
          hasSpecialTax: false
        }
      } else {
        fermerModalMarchandise()
      }
    }

    const poidsBrutConteneurCalcule = computed(() => {
      if (afficherModalConteneur.value && nouvelleMarchandise.value.poidsBrut > 0) {
        return nouvelleMarchandise.value.poidsBrut
      }

      if (indexBLCourant.value === null) return 0

      const bl = listeBLs.value[indexBLCourant.value]
      const numeroConteneur = nouveauConteneur.value.numeroConteneur

      if (!bl || !numeroConteneur) return 0

      let poidsTotal = 0

      if (bl.marchandises && Array.isArray(bl.marchandises)) {
        bl.marchandises.forEach((marchandise: any) => {
          if (marchandise.conteneur?.numeroConteneur === numeroConteneur) {
            poidsTotal += marchandise.poidsBrut || 0
          }
        })
      }

      return poidsTotal
    })

    watch([poidsBrutConteneurCalcule, () => nouveauConteneur.value.numeroConteneur], () => {
      nouveauConteneur.value.poidsBrut = poidsBrutConteneurCalcule.value
    })

    const enregistrerConteneur = () => {
      const conteneur = {
        ...nouveauConteneur.value,
        poidsBrut: poidsBrutConteneurCalcule.value
      }

      nouvelleMarchandise.value.conteneur = conteneur
      nouvelleMarchandise.value.conteneurSelectionne = conteneur.numeroConteneur
      success('Conteneur associé à la marchandise')

      if (!garderModalConteneurOuvert.value) {
        fermerModalConteneur()
      } else {
        nouveauConteneur.value.numeroConteneur = ''
        nouveauConteneur.value.numeroScelle = ''
      }
      indexConteneurEnEdition.value = null
    }

    const supprimerMarchandise = async (blIndex: number, marchandiseIndex: number) => {
      const bl = listeBLs.value[blIndex]
      const marchandise = bl.marchandises[marchandiseIndex]

      const confirmed = await monSwal('Voulez-vous vraiment supprimer cette marchandise ?')
      if (!confirmed) return

      if (marchandise.id && blsExistantsEnBase.value) {
        try {
          await ApiService.delete(`/besc-lignes/${marchandise.id}`)
          bl.marchandises.splice(marchandiseIndex, 1)
          success('Marchandise supprimée avec succès')
        } catch (err: any) {
          error(err?.response?.data?.message || 'Erreur lors de la suppression')
        }
      } else {
        bl.marchandises.splice(marchandiseIndex, 1)
        success('Marchandise supprimée')
      }
    }

    const getStatistiquesBL = (bl: Bl) => {
      return {
        nombreMarchandises: bl.marchandises?.length || 0,
        poidsTotalMarchandises: bl.marchandises?.reduce((sum, m) => sum + (m.poidsBrut || 0), 0) || 0,
        valeurTotale: bl.marchandises?.reduce((sum, m) => sum + (m.valeurDeclaree || 0), 0) || 0
      }
    }

    const mettreAJour = async () => {
      if (!toutValider()) {
        error('Veuillez corriger les erreurs dans les BLs')
        return
      }

      const blsSansMarchandises = listeBLs.value.filter(
        bl => !bl.marchandises || bl.marchandises.length === 0
      )

      if (blsSansMarchandises.length > 0) {
        error('Chaque BL doit avoir au moins une marchandise')
        return
      }

      enregistrementEnCours.value = true

      try {
        const payload = {
          bls: listeBLs.value.map(bl => ({
            id: bl.id || null,
            numeroBL: bl.numeroBL?.trim() || '',
            idCompagnie: bl.idCompagnie,
            idNavire: bl.idNavire || null,
            referenceVoyage: bl.referenceVoyage?.trim() || null,
            poidsBrut: bl.poidsBrut,
            marchandises: bl.marchandises.map(m => ({
              id: m.id || null,
              description: m.description?.trim() || '',
              hscode: m.hscode || null,
              nombreColis: m.nombreColis,
              typeConditionnement: m.typeConditionnement,
              poidsBrut: m.poidsBrut,
              poidsNet: m.poidsNet || 0,
              volume: m.volume || null,
              valeurDeclaree: m.valeurDeclaree,
              devise: m.devise || 'USD',
              paysOrigine: m.paysOrigine,
              tableauIMDG: m.tableauIMDG,
              categorieProduit: m.categorieProduit,
              conteneur: (m as any).conteneur ? {
                ...((m as any).conteneur.id && { id: (m as any).conteneur.id }),
                numeroConteneur: (m as any).conteneur.numeroConteneur,
                numeroScelle: (m as any).conteneur.numeroScelle,
                typeConteneur: typeof (m as any).conteneur.typeConteneur === 'object'
                  ? (m as any).conteneur.typeConteneur?.id
                  : (m as any).conteneur.typeConteneur,
                typeChargement: typeof (m as any).conteneur.typeChargement === 'object'
                  ? (m as any).conteneur.typeChargement?.id
                  : (m as any).conteneur.typeChargement,
                poidsBrut: Number((m as any).conteneur.poidsBrut || 0),
                volume: Number((m as any).conteneur.volume || 0),
                description: (m as any).conteneur.description
              } : null
            }))
          }))
        }

        await ApiService.put(`/besc/${idBesc.value}/bls`, payload)
        success('BLs, marchandises et conteneurs mis à jour avec succès !')

        await chargerBLsExistants()
        router.push({ name: 'EditAddBescStep3', params: { id: idBesc.value } })

      } catch (e: any) {
        error(e?.response?.data?.message || 'Erreur lors de la mise à jour')
      } finally {
        enregistrementEnCours.value = false
      }
    }

    // const blActuel = computed(() => {
    //   if (indexBLCourant.value === null) return null
    //   return listeBLs[indexBLCourant.value]
    // })

    const blActuel = computed(() => {
      if (indexBLCourant.value === null) return null
      return listeBLs.value[indexBLCourant.value]
    })

    const chargerBLsExistants = async () => {
      console.log('🔄 EditAddBescStep2 - chargerBLsExistants appelé');
      try {
        const { data } = await ApiService.get(`/besc/${idBesc.value}/bls`)
        console.log("📦 Réponse BLs:", data.data?.length || 0, "BL(s) trouvé(s)");

        if (data.data && data.data.length > 0) {
          chargementDonnees.value = true
          blsExistantsEnBase.value = true

          listeBLs.value.splice(0, listeBLs.value.length)

          // 🔥 Charger les options depuis les données brutes AVANT le mapping
          for (const bl of data.data) {
            // Charger les options depuis les objets complets dans les données brutes
            if (bl.compagnie) {
              const option = {
                label: `${bl.compagnie.name} ${bl.compagnie.scac_code ? `- ${bl.compagnie.scac_code}` : ''}`,
                value: bl.compagnie.id
              };
              if (!compagniesMaritimes.value.find(c => c.value === bl.compagnie.id)) {
                compagniesMaritimes.value.push(option);
              }
              console.log('Compagnie chargée:', bl.compagnie.name);
            }
            
            if (bl.navire) {
              const option = {
                label: `${bl.navire.name} - MMSI: ${bl.navire.mmsi || 'N/A'} - IMO: ${bl.navire.imo || 'N/A'}`,
                value: bl.navire.id
              };
              if (!listeNavires.value.find(n => n.value === bl.navire.id)) {
                listeNavires.value.push(option);
              }
              console.log('Navire chargé:', bl.navire.name);
            }
          }

          // Maintenant mapper les données pour listeBLs
          listeBLs.value.push(...data.data.map((bl: any) => {
            // Utiliser marchandises si disponible, sinon bescLignes
            const lignes = bl.marchandises || bl.bescLignes || [];
            
            return {
              id: bl.id,
              numeroBL: bl.numeroBL || bl.bl_number,
              idCompagnie: bl.idCompagnie || bl.shipping_line_id,
              idNavire: bl.idNavire || bl.ship_id,
              referenceVoyage: bl.referenceVoyage || bl.voyage_reference || '',
              poidsBrut: parseFloat(bl.poidsBrut || bl.gross_weight || 0),

              marchandises: lignes.map((m: any) => ({
                id: m.id,
                description: m.description || m.descriptionMarchandise,
                hscode: typeof m.hscode === 'object' ? m.hscode?.id : m.hscode,
                nombreColis: parseInt(m.nombreColis || 1),
                typeConditionnement: m.typeConditionnement,
                poidsBrut: parseFloat(m.poidsBrut || 0),
                poidsNet: parseFloat(m.poidsNet || 0),
                volume: parseFloat(m.volume || 0),
                valeurDeclaree: parseFloat(m.valeurDeclaree || 0),
                devise: m.devise || 'USD',
                paysOrigine: typeof m.paysOrigine === 'object' ? m.paysOrigine?.id : m.paysOrigine,
                tableauIMDG: typeof m.tableauIMDG === 'object' ? m.tableauIMDG?.id : m.tableauIMDG,
                categorieProduit: typeof m.categorieProduit === 'object' ? m.categorieProduit?.id : m.categorieProduit,

                conteneur: m.conteneur ? {
                  id: m.conteneur.id,
                  numeroConteneur: m.conteneur.numeroConteneur,
                  numeroScelle: m.conteneur.numeroScelle,
                  typeConteneur: m.conteneur.typeConteneur?.id || m.conteneur.typeConteneur,
                  typeChargement: m.conteneur.typeChargement?.id || m.conteneur.typeChargement,
                  poidsBrut: parseFloat(m.conteneur.poidsBrut || 0),
                  volume: parseFloat(m.conteneur.volume || 0),
                  description: m.conteneur.description
                } : null
              })),
              erreurs: {}
            };
          }))

          success(`${listeBLs.value.length} BL(s) chargé(s) avec succès`)
        } else {
          blsExistantsEnBase.value = false
          if (listeBLs.value.length === 0) {
            ajouterBL()
          }
        }
      } catch (err: any) {
        console.error('Erreur chargement BLs:', err)
        error('Impossible de vérifier les BLs existants')
        blsExistantsEnBase.value = false
      } finally {
        chargementDonnees.value = false
      }
    }

    onMounted(async () => {
      console.log('EditAddBescStep2 - onMounted appelé');
      const idFromRoute = route.params.id as string
      console.log('🔍 ID depuis route:', idFromRoute);

      if (!idFromRoute) {
        error('ID BESC manquant. Retour à la liste.')
        router.push({ name: 'ListeBesc' })
        return
      }

      idBesc.value = idFromRoute
      // Supprimé: bescWizard.bescId = idFromRoute (pas nécessaire en mode édition)

      // Supprimé: await loadBescProgressionFromAPI(idFromRoute) (pas nécessaire en mode édition)

      await Promise.all([
        fetchCodeSH(),
        fetchPays(),
        fetchTypeConteneur(),
        fetchChargement(),
        fetchTableauxIMDG(),
        fetchCategoriesProduit()
      ])

      await chargerBLsExistants()
      await fetchRequestCorrection()
    })

    const checkBlacklist = async (type: string, referenceValue: string) => {
      try {
        const response = await ApiService.get(
          `/regulatory/check?entry_type=${type}&reference_value=${referenceValue}`
        );
        return response.data.data;
      } catch (err) {
        console.error('Erreur vérification blacklist:', err);
        return { isBlacklisted: false, details: null };
      }
    }

    const checkRestriction = async (
      hscode: string | null, 
      categorieProduit: string | null, 
      paysOrigine: string | null
    ) => {
      try {
        const paysOrigineAUtiliser = paysOrigine || bescWizard.step1.paysOrigine;

        if (!paysOrigineAUtiliser) return { isRestricted: false, restrictions: [] };

        const response = await ApiService.post('/restricted/check', {
          hscode,
          categorieProduit,
          paysOrigine: paysOrigineAUtiliser,
          poidsBrut: nouvelleMarchandise.value.poidsBrut,    
          valeurDeclaree: nouvelleMarchandise.value.valeurDeclaree 
        });

        return response.data.data;
      } catch (err) {
        console.error('Erreur vérification restrictions:', err);
        return { isRestricted: false, restrictions: [] };
      }
    }

    const onHscodeChange = async (val: any, setValue: any) => {
      setValue(val);
      if (!nouvelleMarchandise.value) return;
      const result = await checkRestriction(
        val,
        nouvelleMarchandise.value.categorieProduit,
        nouvelleMarchandise.value.paysOrigine
      );
      restrictionAlerts.value.marchandise = result.isRestricted ? result.restrictions : null;
    }

    const onCategorieProduitChange = async (val: any, setValue: any) => {
      setValue(val);
      if (!nouvelleMarchandise.value) return;
      const result = await checkRestriction(
        nouvelleMarchandise.value.hscode,
        val,
        nouvelleMarchandise.value.paysOrigine
      );
      restrictionAlerts.value.marchandise = result.isRestricted ? result.restrictions : null;
    }

    return {
      peutAjouterBL,
      listeBLs,  
      blSchema,
      blForm,
      bescId,
      ajouterBL,
      supprimerBL,
      validerBL,
      toutValider,
      rechercherCompagnie,
      rechercherNavire,
      ouvrirModalMarchandise,
      afficherModalMarchandise,
      afficherModalConteneur,
      nouvelleMarchandise,
      nouveauConteneur,
      enregistrerMarchandise,
      enregistrerConteneur,
      poidsTotalBrut,
      nombreCompagniesUniques,
      nombreBLValides,
      peutContinuer,
      enregistrementEnCours,
      mettreAJour,
      blActuel,
      hsCodes,
      pays,
      loadingRefs,
      compagniesMaritimes,
      listeNavires,
      chargementCompagnies,
      chargementNavires,
      typeConteneurs,
      typeConditionnements,
      typeChargements,
      supprimerMarchandise,
      indexConteneurEnEdition,
      getStatistiquesBL,
      deviseOptions,
      tableauxIMDG,
      categoriesProduit,
      fetchTableauxIMDG,
      fetchCategoriesProduit,
      garderModalOuvert,
      garderModalConteneurOuvert,
      idBesc,
      chargementDonnees,
      isEditMode,
      blsExistantsEnBase,
      marchandiseSchema,
      fermerModalMarchandise,
      fermerModalConteneur,
      conteneurSchema,
      ajouterConteneurALaMarchandise,
      indexBLCourant,
      chargerBLsExistants,
      afficherSelectConteneur,
      resetConteneursSelonType,
      afficherModalVoirConteneurs,
      marchandiseSelectionnee,
      afficherConteneursModal,
      fermerModalVoirConteneurs,
      modifierMarchandise,
      indexMarchandiseEnEdition,
      tousLesConteneursDisponiblesDuBL,
      ajouterConteneurExistantAMarchandise,
      poidsBrutConteneurCalcule,
      calculerConteneurUniques,
      obtenirConteneursUniques,
      obtenirMarchandisesVrac,
      afficherMarchandisesConteneur,
      afficherModalMarchandisesConteneur,
      conteneurSelectionne,
      marchandisesConteneurSelectionne,
      fermerModalMarchandisesConteneur,
      blIndex,
      calculerPoidsBrutBL,
      corrections,
      markCorrectionAsFixed,
      blacklistAlerts,
      checkBlacklist,
      restrictionAlerts,
      checkRestriction,
      onHscodeChange,
      onCategorieProduitChange
    }
  }
})
</script>

<style scoped>
.btn-outline-primary:hover .badge {
  background-color: #fff !important;
  color: #0d6efd !important;
}

.btn-outline-primary {
  transition: all 0.3s ease;
}

.btn-outline-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.modal-backdrop.conteneur-backdrop {
  z-index: 1060 !important;
}

.modal.conteneur-modal {
  z-index: 1061 !important;
}

.modal.marchandise-modal {
  z-index: 1055 !important;
}

.modal-backdrop.marchandise-backdrop {
  z-index: 1050 !important;
}

.section-title h6::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 18px;
  background-color: #0a59a4;
  border-radius: 2px;
}

.section-title h6 {
  position: relative;
  padding-left: 12px;
}

.card-header {
  border-bottom: 2px solid #e9ecef;
}

.badge {
  padding: 0.5rem 1rem;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.btn-primary:hover,
.btn-primary:focus,
.btn-primary:active {
  background-color: #0a59a4 !important;
  border-color: #0a59a4 !important;
  color: #fff !important;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}
</style>